/**
 * 会社状態管理フック
 * @module hooks/useCompanyState
 */

import { trpc } from '../lib/trpc';
import type { Company } from '../types/company';
import type { CancellationReason } from '@shared/types/cancellation';

/**
 * 会社のアクティブ状態
 */
export type CompanyActiveState =
  | 'active'           // 通常利用中
  | 'suspended'        // 解約予約中（契約期間内）
  | 'expired'          // 契約期限切れ（データ保全期間内）
  | 'scheduled_delete' // 削除予定
  | 'deleted';         // 削除済み

/**
 * 会社のアクティブ状態を計算（クライアント側）
 */
export function getCompanyActiveState(company: Company | undefined | null): CompanyActiveState {
  if (!company) return 'active';

  const dataDeletedAt = company.dataDeletedAt ?? company.deletedAt;
  const dataDeletionScheduledAt = company.dataDeletionScheduledAt;

  // 物理削除済み
  if (dataDeletedAt) {
    return 'deleted';
  }

  // 削除予定日を過ぎている
  if (
    dataDeletionScheduledAt &&
    new Date() >= new Date(dataDeletionScheduledAt)
  ) {
    return 'scheduled_delete';
  }

  // 契約終了済み（契約切れ）
  if (company.subscriptionStatus === 'canceled' && !company.isActive) {
    return 'expired';
  }

  // 解約予約中（期末解約）
  if (company.subscriptionStatus === 'canceling') {
    return 'suspended';
  }

  // 通常利用中
  return 'active';
}

/**
 * データ削除までの残り日数を計算
 */
export function getDaysUntilDeletion(company: Company | undefined | null): number | null {
  if (!company?.dataDeletionScheduledAt) {
    return null;
  }

  const now = new Date();
  const deletionDate = new Date(company.dataDeletionScheduledAt);
  const diffTime = deletionDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0;
}

/**
 * 会社が復活可能かどうかを判定
 */
export function isCompanyReactivatable(company: Company | undefined | null): boolean {
  if (!company) return false;

  const state = getCompanyActiveState(company);

  // 契約切れまたは削除予定の場合のみ復活可能
  if (state !== 'expired' && state !== 'scheduled_delete') {
    return false;
  }

  // 既に物理削除済みの場合は復活不可
  if (company.dataDeletedAt ?? company.deletedAt) {
    return false;
  }

  // 削除予定日を過ぎている場合は復活不可
  if (
    company.dataDeletionScheduledAt &&
    new Date() >= new Date(company.dataDeletionScheduledAt)
  ) {
    return false;
  }

  return true;
}

/**
 * 会社状態フック
 */
export function useCompanyState(companyId?: string) {
  const { data: stateData, isLoading, error } = trpc.subscriptionCancellation.getCompanyState.useQuery(
    { companyId: companyId! },
    { enabled: !!companyId }
  );

  return {
    activeState: stateData?.activeState,
    stateMessage: stateData?.stateMessage,
    subscriptionStatus: stateData?.subscriptionStatus,
    isActive: stateData?.isActive,
    cancelRequestedAt: stateData?.cancelRequestedAt,
    cancelEffectiveDate: stateData?.cancelEffectiveDate,
    daysUntilDeletion: stateData?.daysUntilDeletion,
    isLoading,
    error,
  };
}

/**
 * 削除予定フック
 */
export function useDeletionSchedule(companyId?: string) {
  const { data, isLoading, error } = trpc.subscriptionCancellation.getDeletionSchedule.useQuery(
    { companyId: companyId! },
    { enabled: !!companyId }
  );

  return {
    subscriptionEndsAt: data?.subscriptionEndsAt,
    dataRetentionPeriodDays: data?.dataRetentionPeriodDays,
    dataDeletionScheduledAt: data?.dataDeletionScheduledAt,
    daysUntilDeletion: data?.daysUntilDeletion,
    activeState: data?.activeState,
    stateMessage: data?.stateMessage,
    isReactivatable: data?.isReactivatable,
    isReactivationEnabled: data?.isReactivationEnabled,
    isLoading,
    error,
  };
}

/**
 * 解約処理フック（v2.1: 拡張）
 */
export function useCancelSubscription() {
  const cancelMutation = trpc.subscriptionCancellation.cancel.useMutation();

  const cancel = async (params: {
    companyId: string;
    reason?: CancellationReason;
    reasonDetail?: string;
    feedback?: string;
  }) => {
    return await cancelMutation.mutateAsync({
      companyId: params.companyId,
      reason: params.reason,
      reasonDetail: params.reasonDetail,
      feedback: params.feedback,
    });
  };

  return {
    cancel,
    mutateAsync: cancelMutation.mutateAsync,  // 直接mutateAsyncも公開
    isLoading: cancelMutation.isPending,
    isPending: cancelMutation.isPending,
    error: cancelMutation.error,
  };
}

/**
 * 解約キャンセルフック（v2.1: 拡張）
 */
export function useUndoCancellation() {
  const undoMutation = trpc.subscriptionCancellation.undoCancellation.useMutation();

  const undo = async (params: {
    companyId: string;
    undoneReason?: string;
  }) => {
    return await undoMutation.mutateAsync({
      companyId: params.companyId,
      undoneReason: params.undoneReason,
    });
  };

  return {
    undo,
    mutateAsync: undoMutation.mutateAsync,  // 直接mutateAsyncも公開
    isLoading: undoMutation.isPending,
    isPending: undoMutation.isPending,
    error: undoMutation.error,
  };
}

/**
 * 復活処理フック
 */
export function useReactivateSubscription() {
  const reactivateMutation = trpc.subscriptionCancellation.reactivate.useMutation();

  const reactivate = async (companyId: string, planCode: string) => {
    return await reactivateMutation.mutateAsync({
      companyId,
      planCode,
    });
  };

  return {
    reactivate,
    isLoading: reactivateMutation.isPending,
    isPending: reactivateMutation.isPending,
    error: reactivateMutation.error,
  };
}

/**
 * [v2.1 新規] 解約権限チェックフック
 */
export function useCanCancelSubscription(companyId: string | undefined) {
  const { data, isLoading, error } = trpc.subscriptionCancellation.canCancel.useQuery(
    { companyId: companyId! },
    {
      enabled: !!companyId,
      staleTime: 5 * 60 * 1000, // 5分間キャッシュ
    }
  );

  return {
    canCancel: data?.canCancel ?? false,
    isLoading,
    error,
  };
}

/**
 * [v2.1 新規] 最新の解約申請取得フック
 */
export function useLatestCancellationRequest(companyId: string | undefined) {
  const { data, isLoading, error } = trpc.subscriptionCancellation.getLatestCancellationRequest.useQuery(
    { companyId: companyId! },
    {
      enabled: !!companyId,
      staleTime: 1 * 60 * 1000, // 1分間キャッシュ
    }
  );

  return {
    latestRequest: data,
    isLoading,
    error,
  };
}

/**
 * [v2.1 新規] 解約履歴取得フック
 */
export function useCancellationHistory(companyId: string | undefined) {
  const { data, isLoading, error } = trpc.subscriptionCancellation.getCancellationHistory.useQuery(
    { companyId: companyId! },
    {
      enabled: !!companyId,
      staleTime: 5 * 60 * 1000, // 5分間キャッシュ
    }
  );

  return {
    history: data || [],
    isLoading,
    error,
  };
}
